/**
 * Shared GA4 Consent Mode gate. Reads consent directly from the page's
 * existing window.dataLayer -- never introduces its own consent mechanism,
 * never writes a cookie/localStorage/sessionStorage entry.
 *
 * Multiple independently-created gates (one per tracker instance) can
 * coexist against the same live dataLayer.push: the wrap installed by the
 * first gate carries a Set of listener callbacks that later gates simply
 * join, and the original push is restored only once the last listener
 * leaves. This avoids the single-owner-flag trap where a second gate would
 * see "already wrapped" and silently never observe live consent updates.
 */

type ConsentPayload = Record<string, string>;
type ConsentState = Record<string, string>;

interface WrappedPush {
  (...args: unknown[]): number;
  __liftConsentListeners: Set<(entry: unknown) => void>;
  __liftOriginalPush: (...args: unknown[]) => number;
}

type LiftDataLayer = unknown[] & {
  push: ((...items: unknown[]) => number) | WrappedPush;
};

// Recognizes both gtag()-style consent calls (which, since gtag(){dataLayer.push(arguments)},
// land in the array as arguments-like entries: ["consent", "default"|"update", {...}])
// and plain-object CMP pushes shaped like { consent: { analytics_storage: "granted" } }.
function extractConsentPayload(entry: unknown): ConsentPayload | null {
  if (!entry) return null;
  const isArrayLike =
    Array.isArray(entry) ||
    (typeof entry === "object" &&
      typeof (entry as { length?: unknown }).length === "number");
  if (isArrayLike) {
    const arrLike = entry as ArrayLike<unknown>;
    if (
      arrLike[0] === "consent" &&
      arrLike[2] &&
      typeof arrLike[2] === "object"
    ) {
      return arrLike[2] as ConsentPayload;
    }
  }
  if (
    typeof entry === "object" &&
    entry !== null &&
    "consent" in entry &&
    typeof (entry as { consent: unknown }).consent === "object"
  ) {
    return (entry as { consent: ConsentPayload }).consent;
  }
  return null;
}

function getDataLayerArray(): LiftDataLayer | null {
  if (typeof window === "undefined") return null;
  const win = window as unknown as { dataLayer?: LiftDataLayer };
  if (!Array.isArray(win.dataLayer)) {
    win.dataLayer = win.dataLayer || ([] as unknown as LiftDataLayer);
  }
  return win.dataLayer as LiftDataLayer;
}

// MUST capture whatever push is currently installed, NOT Array.prototype.push.
// By the time this runs, GTM (and any other tag manager) may have replaced
// dataLayer.push with its own override, and that override IS how GTM
// observes events. Binding the raw array method discards it, so every later
// push lands in the array but is invisible to GTM.
function ensureObserved(
  dataLayer: LiftDataLayer | null,
  onEntry: (entry: unknown) => void
): void {
  if (!dataLayer) return;
  const existingPush = dataLayer.push as WrappedPush;
  if (existingPush && existingPush.__liftConsentListeners) {
    existingPush.__liftConsentListeners.add(onEntry);
    return;
  }

  const originalPush: (...args: unknown[]) => number =
    typeof dataLayer.push === "function"
      ? (dataLayer.push as (...args: unknown[]) => number).bind(dataLayer)
      : Array.prototype.push.bind(dataLayer);

  const listeners = new Set<(entry: unknown) => void>([onEntry]);
  function wrappedPush(...args: unknown[]): number {
    const result = originalPush(...args);
    args.forEach((entry) => {
      listeners.forEach((fn) => {
        try {
          fn(entry);
        } catch (error) {
          // One listener's error must not break another's, or swallow originalPush's result.
        }
      });
    });
    return result;
  }
  (wrappedPush as unknown as WrappedPush).__liftConsentListeners = listeners;
  (wrappedPush as unknown as WrappedPush).__liftOriginalPush = originalPush;
  dataLayer.push = wrappedPush as unknown as LiftDataLayer["push"];
}

// Undoes ensureObserved()'s registration for this one listener. Only restores
// the original push once the LAST listener leaves -- if something else (e.g.
// GTM) re-wrapped on top of us since, we leave it alone rather than
// clobbering its override.
function teardownObserver(
  dataLayer: LiftDataLayer | null,
  onEntry: (entry: unknown) => void
): void {
  const push = dataLayer && (dataLayer.push as WrappedPush);
  if (!push || !push.__liftConsentListeners) return;
  push.__liftConsentListeners.delete(onEntry);
  if (
    push.__liftConsentListeners.size === 0 &&
    dataLayer &&
    dataLayer.push === push
  ) {
    dataLayer.push = push.__liftOriginalPush as unknown as LiftDataLayer["push"];
  }
}

export interface CreateConsentGateOptions {
  defaultConsentState?: "granted" | "denied";
  onGrantedChange?: (granted: boolean) => void;
}

export interface ConsentGate {
  isGranted(): boolean;
  effectiveAnalyticsStorage(): string;
  getConsentState(): ConsentState;
  getDataLayer(): LiftDataLayer | null;
  bootstrapOnce(): void;
  reobserve(): void;
  isObserving(): boolean;
  teardown(): void;
}

export function createConsentGate({
  defaultConsentState = "granted",
  onGrantedChange,
}: CreateConsentGateOptions = {}): ConsentGate {
  const consentState: ConsentState = {};
  let bootstrapped = false;
  let observing = false;
  let destroyed = false;

  function onEntry(entry: unknown) {
    if (destroyed) return;
    const payload = extractConsentPayload(entry);
    if (payload) onConsentSignal(payload, { historical: false });
  }

  function mergeConsentHistorical(payload: ConsentPayload) {
    Object.keys(payload).forEach((key) => {
      if (!(key in consentState)) consentState[key] = payload[key];
    });
  }

  function mergeConsentLive(payload: ConsentPayload) {
    Object.keys(payload).forEach((key) => {
      consentState[key] = payload[key];
    });
  }

  function effectiveAnalyticsStorage(): string {
    return "analytics_storage" in consentState
      ? consentState.analytics_storage
      : defaultConsentState;
  }

  function isGranted(): boolean {
    return effectiveAnalyticsStorage() === "granted";
  }

  function onConsentSignal(
    payload: ConsentPayload,
    { historical }: { historical: boolean }
  ) {
    if (destroyed) return;
    const before = isGranted();
    if (historical) {
      mergeConsentHistorical(payload);
    } else {
      mergeConsentLive(payload);
    }
    const after = isGranted();
    if (before !== after && typeof onGrantedChange === "function") {
      onGrantedChange(after);
    }
  }

  function reobserve(): void {
    if (destroyed) return;
    const dataLayer = getDataLayerArray();
    if (!dataLayer) return;
    ensureObserved(dataLayer, onEntry);
    observing = true;
  }

  function bootstrapOnce(): void {
    if (bootstrapped || destroyed) return;
    bootstrapped = true;
    const dataLayer = getDataLayerArray();
    if (dataLayer) {
      for (let i = dataLayer.length - 1; i >= 0; i--) {
        const payload = extractConsentPayload(dataLayer[i]);
        if (payload) mergeConsentHistorical(payload);
      }
    }
    reobserve();
  }

  function teardown(): void {
    if (destroyed) return;
    destroyed = true;
    teardownObserver(getDataLayerArray(), onEntry);
    observing = false;
  }

  return {
    isGranted,
    effectiveAnalyticsStorage,
    getConsentState: () => ({ ...consentState }),
    getDataLayer: getDataLayerArray,
    bootstrapOnce,
    reobserve,
    isObserving: () => observing,
    teardown,
  };
}
